import React, { useState } from "react";
import axios from "axios";
import { API_BASE_URL, LOGO_URL } from "../../../config";
import { EyeIcon, EyeOffIcon, KeyIcon, UserIcon, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { LoginModalProps } from "../../../types/auth";

const API_URL = `${API_BASE_URL}/user/login`;

export default function LoginModal({
    isOpen,
    onClose,
    onForgotPassword,
    onSwitchToRegister,
    onSuccess,
}: LoginModalProps) {
    const [formData, setFormData] = useState({
        userName: "",
        password: "",
    });

    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [successMessage, setSuccessMessage] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);
    const navigate = useNavigate();

    if (!isOpen) return null;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
        if (errorMessage) setErrorMessage("");
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setErrorMessage("");
        setSuccessMessage("");

        try {
            const response = await axios.post(API_URL, {
                userName: formData.userName,
                password: formData.password,
            });

            if (response.status === 200) {
                const token = response.data?.data?.token;
                if (token) {
                    localStorage.setItem("authToken", token);
                    onSuccess?.(token);
                }

                setSuccessMessage("Login successful!");

                setTimeout(() => {
                    onClose();
                    navigate("/applicant/dashboard");
                }, 800);
            }
        } catch (error: any) {
            setErrorMessage(
                error.response?.data?.message ||
                "Invalid credentials. Please try again."
            );
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-5 backdrop-blur-sm">
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md p-8 animate-in fade-in zoom-in duration-300 space-y-6">
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-gray-400 hover:text-black transition-colors"
                >
                    ✕
                </button>

                <div className="text-center">
                    <img
                        src={LOGO_URL}
                        alt="Company Logo"
                        className="mx-auto h-12 w-auto mb-6"
                    />
                    <h2 className="text-2xl font-bold text-gray-900">Welcome back</h2>
                    <p className="mt-2 text-sm text-gray-500">
                        Sign in to your account
                    </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                    {errorMessage && (
                        <div className="p-4 text-sm text-red-700 bg-red-50 rounded-lg border border-red-200 flex items-start space-x-3">
                             <div className="flex-shrink-0 mt-0.5">
                                <svg className="h-5 w-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <div>
                                <h3 className="font-bold text-sm mb-1">Login Error</h3>
                                <p className="text-xs leading-relaxed">{errorMessage}</p>
                            </div>
                        </div>
                    )}

                    {successMessage && (
                        <div className="p-4 text-sm text-green-700 bg-green-50 rounded-lg border border-green-200 flex items-start space-x-3">
                            <div className="flex-shrink-0 mt-0.5">
                                <svg className="h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <div>
                                <h3 className="font-bold text-sm mb-1">Success</h3>
                                <p className="text-xs leading-relaxed">{successMessage}</p>
                            </div>
                        </div>
                    )}

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Username
                        </label>
                        <div className="relative group">
                            <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400 group-focus-within:text-[#35A854] transition-colors">
                                <UserIcon className="size-5" />
                            </span>
                            <input
                                type="text"
                                name="userName"
                                autoComplete="username"
                                placeholder="Enter your username"
                                required
                                disabled={isLoading}
                                value={formData.userName}
                                onChange={handleChange}
                                className="w-full bg-[#f8f9fa] border border-gray-200 rounded-lg pl-10 pr-4 py-2.5 focus:ring-2 focus:ring-[#35A854] focus:border-[#35A854] outline-none transition disabled:bg-gray-100"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Password
                        </label>
                        <div className="relative group">
                            <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400 group-focus-within:text-[#35A854] transition-colors">
                                <KeyIcon className="size-5" />
                            </span>
                            <input
                                type={showPassword ? "text" : "password"}
                                name="password"
                                autoComplete="current-password"
                                placeholder="Enter your password"
                                required
                                disabled={isLoading}
                                value={formData.password}
                                onChange={handleChange}
                                className="w-full bg-[#f8f9fa] border border-gray-200 rounded-lg pl-10 pr-10 py-2.5 focus:ring-2 focus:ring-[#35A854] focus:border-[#35A854] outline-none transition disabled:bg-gray-100"
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                            >
                                {showPassword ? (
                                    <EyeOffIcon className="size-5" />
                                ) : (
                                    <EyeIcon className="size-5" />
                                )}
                            </button>
                        </div>
                    </div>

                    <div className="flex items-center justify-between">
                        <div className="flex items-center">
                            <input
                                id="remember-me"
                                type="checkbox"
                                checked={rememberMe}
                                onChange={(e) => setRememberMe(e.target.checked)}
                                className="h-4 w-4 text-[#35A854] border-gray-300 rounded focus:ring-[#35A854]"
                            />
                            <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-600">Remember me</label>
                        </div>
                        <div className="text-sm">
                            <button
                                type="button"
                                onClick={onForgotPassword}
                                className="font-semibold text-gray-900 hover:text-[#35A854] transition-colors"
                            >
                                Forgot password?
                            </button>
                        </div>
                    </div>

                    <button
                        type="submit"
                        disabled={isLoading}
                        className={`w-full py-2.5 rounded-lg font-semibold text-white transition-all
                            flex items-center justify-center gap-2 shadow-sm
                            ${isLoading
                                ? "bg-[#35A854]/60 cursor-not-allowed"
                                : "brand-bg brand-hover-bg hover:shadow-md active:scale-[0.98]"
                            }`}
                    >
                        {isLoading ? (
                            <>
                                <Loader2 className="w-5 h-5 animate-spin" />
                                <span>Signing in...</span>
                            </>
                        ) : (
                            "Sign in"
                        )}
                    </button>

                    <div className="text-center mt-6">
                        <p className="text-sm text-gray-500">
                            Don't have an account?{" "}
                            <button
                                type="button"
                                onClick={onSwitchToRegister}
                                className="font-semibold text-gray-900 hover:text-[#35A854] transition-colors"
                            >
                                Create Account
                            </button>
                        </p>
                    </div>
                </form>
            </div>
        </div>
    );
}
